import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Play, Send, Clock, Users, MessageCircle, Radio, Eye, CalendarClock,
  ChevronRight, Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CountdownTimer({ scheduledAt, onLive }) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!scheduledAt) return;
    const target = new Date(scheduledAt).getTime();
    const tick = () => {
      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) {
        setTimeLeft(null);
        onLive?.();
        return;
      }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeLeft({ days, hours, minutes, seconds });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [scheduledAt, onLive]);

  if (!timeLeft) return null;

  const units = [
    { label: 'Days', value: timeLeft.days },
    { label: 'Hours', value: timeLeft.hours },
    { label: 'Minutes', value: timeLeft.minutes },
    { label: 'Seconds', value: timeLeft.seconds },
  ];

  return (
    <div data-testid="countdown-timer" className="text-center py-8">
      <div className="flex items-center justify-center gap-1 mb-4">
        <Radio className="w-4 h-4 text-red-500 animate-pulse" />
        <span className="text-sm font-medium text-red-600 uppercase tracking-wider">Going Live In</span>
      </div>
      <div className="flex items-center justify-center gap-3">
        {units.map((u) => (
          <div key={u.label} className="bg-slate-900 text-white rounded-xl px-4 py-3 min-w-[70px]">
            <p className="text-2xl sm:text-3xl font-bold font-mono">{String(u.value).padStart(2, '0')}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">{u.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function LiveChat({ chatEnabled }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [userName, setUserName] = useState('');
  const [nameSet, setNameSet] = useState(false);
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);
  const lastTimestamp = useRef('');

  const fetchMessages = useCallback(async () => {
    try {
      const url = lastTimestamp.current
        ? `${API}/webinar/chat?since=${encodeURIComponent(lastTimestamp.current)}`
        : `${API}/webinar/chat`;
      const { data } = await axios.get(url);
      if (data.length > 0) {
        setMessages(prev => {
          const existing = new Set(prev.map(m => m.created_at + m.user_name));
          const newMsgs = data.filter(m => !existing.has(m.created_at + m.user_name));
          return [...prev, ...newMsgs];
        });
        lastTimestamp.current = data[data.length - 1].created_at;
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!chatEnabled) return;
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [chatEnabled, fetchMessages]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || !userName.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/webinar/chat`, { message: input.trim(), user_name: userName.trim() });
      setInput('');
      await fetchMessages();
    } catch {}
    setSending(false);
  };

  if (!chatEnabled) return null;

  if (!nameSet) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6" data-testid="chat-name-prompt">
        <MessageCircle className="w-10 h-10 text-slate-300 mb-4" />
        <p className="text-sm text-slate-600 mb-4 text-center">Enter your name to join the live chat</p>
        <form onSubmit={(e) => { e.preventDefault(); if (userName.trim()) setNameSet(true); }} className="w-full max-w-xs space-y-3">
          <Input
            value={userName}
            onChange={e => setUserName(e.target.value)}
            placeholder="Your name"
            className="text-sm"
            data-testid="chat-name-input"
          />
          <Button type="submit" disabled={!userName.trim()} className="w-full rounded-full bg-slate-900 text-white text-sm" data-testid="chat-join-btn">
            Join Chat
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="live-chat">
      {/* Chat header */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        <span className="text-sm font-medium text-slate-700">Live Chat</span>
        <span className="text-xs text-slate-400 ml-auto">{messages.length} messages</span>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0" data-testid="chat-messages">
        {messages.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-8">Be the first to say something!</p>
        )}
        {messages.map((msg, i) => (
          <div key={i} className="flex gap-2 items-start">
            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-indigo-600">
              {msg.user_name?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-700">{msg.user_name}</p>
              <p className="text-sm text-slate-600 break-words">{msg.message}</p>
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>
      {/* Input */}
      <form onSubmit={sendMessage} className="p-3 border-t border-slate-100 flex gap-2" data-testid="chat-send-form">
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 text-sm h-9"
          disabled={sending}
          data-testid="chat-message-input"
        />
        <Button type="submit" size="sm" disabled={sending || !input.trim()} className="rounded-full bg-slate-900 text-white h-9 w-9 p-0" data-testid="chat-send-btn">
          <Send className="w-3.5 h-3.5" />
        </Button>
      </form>
    </div>
  );
}

export default function WebinarPage() {
  const navigate = useNavigate();
  const [webinar, setWebinar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [viewMode, setViewMode] = useState('scheduled'); // 'scheduled' | 'ondemand'
  const [accessEmail, setAccessEmail] = useState('');
  const [accessChecking, setAccessChecking] = useState(false);
  const [needsPayment, setNeedsPayment] = useState(false);

  const checkAccess = useCallback(async (email = '') => {
    try {
      const params = email ? `?email=${encodeURIComponent(email)}` : '';
      const { data } = await axios.get(`${API}/webinar/access${params}`);
      setWebinar(data);

      if (!data.has_access) {
        setNeedsPayment(true);
        return;
      }

      setNeedsPayment(false);

      // Determine if we're currently live
      if (data.scheduled_at) {
        const scheduled = new Date(data.scheduled_at).getTime();
        const now = Date.now();
        if (now >= scheduled && now <= scheduled + 4 * 3600000) {
          setIsLive(true);
          setViewMode('scheduled');
        } else if (now < scheduled) {
          setViewMode('scheduled');
        } else {
          setViewMode('ondemand');
        }
      } else {
        setViewMode('ondemand');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load webinar');
    } finally {
      setLoading(false);
      setAccessChecking(false);
    }
  }, []);

  useEffect(() => {
    // Check if email is stored from previous access
    const storedEmail = localStorage.getItem('webinar_access_email');
    if (storedEmail) {
      setAccessEmail(storedEmail);
      checkAccess(storedEmail);
    } else {
      checkAccess('');
    }
  }, [checkAccess]);

  const handleAccessSubmit = (e) => {
    e.preventDefault();
    if (!accessEmail.trim()) return;
    setAccessChecking(true);
    localStorage.setItem('webinar_access_email', accessEmail.trim());
    checkAccess(accessEmail.trim());
  };

  const handleGoLive = useCallback(() => {
    setIsLive(true);
  }, []);

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="animate-pulse text-slate-400">Loading webinar...</div>
      </div>
    );
  }

  if (error || !webinar) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="text-center">
          <Lock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-slate-900 mb-2">Webinar Not Available</h2>
          <p className="text-slate-500 text-sm mb-6">{error || 'This webinar is not currently available.'}</p>
          <Button onClick={() => navigate('/')} variant="outline" className="rounded-full gap-2">
            Go Home <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Paywall: require email verification or payment
  if (needsPayment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-amber-50 flex items-center justify-center px-4" data-testid="webinar-paywall">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-lg p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-5">
              <Lock className="w-8 h-8 text-indigo-600" />
            </div>
            <h2 className="font-serif text-2xl text-slate-900 mb-2" data-testid="paywall-title">{webinar?.title || 'Webinar Access'}</h2>
            <p className="text-slate-500 text-sm mb-6">
              This webinar is available for registered Masterclass participants. Enter your registered email to access, or register for the Masterclass.
            </p>
            <form onSubmit={handleAccessSubmit} className="space-y-3 mb-4">
              <Input
                type="email"
                value={accessEmail}
                onChange={e => setAccessEmail(e.target.value)}
                placeholder="Enter your registered email"
                className="text-sm"
                required
                data-testid="access-email-input"
              />
              <Button type="submit" disabled={accessChecking || !accessEmail.trim()} className="w-full rounded-full bg-slate-900 text-white" data-testid="access-check-btn">
                {accessChecking ? 'Checking...' : 'Access Webinar'}
              </Button>
            </form>
            <p className="text-xs text-slate-400 mb-4">Use the same email you registered with for the Masterclass</p>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-sm text-slate-600 mb-3">Don't have access yet?</p>
              <Button onClick={() => navigate('/checkout')} variant="outline" className="rounded-full gap-2 w-full" data-testid="paywall-register-btn">
                Register for Masterclass (₹499) <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  const hasSchedule = !!webinar.scheduled_at;
  const scheduledTime = hasSchedule ? new Date(webinar.scheduled_at) : null;
  const isBeforeSchedule = hasSchedule && Date.now() < scheduledTime.getTime();
  const showVideo = viewMode === 'ondemand' || isLive || (hasSchedule && !isBeforeSchedule);

  return (
    <div data-testid="webinar-page" className="min-h-screen bg-slate-950">
      {/* Top Bar */}
      <div className="bg-slate-900 border-b border-slate-800 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isLive && (
              <div className="flex items-center gap-1.5 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse" data-testid="live-badge">
                <Radio className="w-3 h-3" /> LIVE
              </div>
            )}
            <h1 className="text-white font-serif text-lg sm:text-xl truncate" data-testid="webinar-title">{webinar.title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {webinar.allow_on_demand && hasSchedule && (
              <div className="hidden sm:flex bg-slate-800 rounded-full p-0.5">
                <button
                  onClick={() => setViewMode('scheduled')}
                  className={`text-xs px-3 py-1.5 rounded-full transition-colors ${viewMode === 'scheduled' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}
                  data-testid="mode-scheduled"
                >
                  <CalendarClock className="w-3 h-3 inline mr-1" /> Scheduled
                </button>
                <button
                  onClick={() => setViewMode('ondemand')}
                  className={`text-xs px-3 py-1.5 rounded-full transition-colors ${viewMode === 'ondemand' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}
                  data-testid="mode-ondemand"
                >
                  <Play className="w-3 h-3 inline mr-1" /> On Demand
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto">
        <div className={`flex flex-col ${webinar.chat_enabled ? 'lg:flex-row' : ''}`}>
          {/* Video Area */}
          <div className={`flex-1 ${webinar.chat_enabled ? 'lg:border-r lg:border-slate-800' : ''}`}>
            {showVideo ? (
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }} data-testid="video-container">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube-nocookie.com/embed/${webinar.youtube_id}?autoplay=1&rel=0&modestbranding=1&origin=${window.location.origin}`}
                  title={webinar.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="youtube-player"
                />
              </div>
            ) : (
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-indigo-950 flex flex-col items-center justify-center" data-testid="countdown-container">
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                    <img
                      src={`https://img.youtube.com/vi/${webinar.youtube_id}/maxresdefault.jpg`}
                      alt="Webinar thumbnail"
                      className="absolute inset-0 w-full h-full object-cover opacity-20"
                    />
                    <div className="relative z-10">
                      <CountdownTimer scheduledAt={webinar.scheduled_at} onLive={handleGoLive} />
                      <p className="text-white/60 text-sm text-center mt-4">
                        {scheduledTime?.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        {' at '}
                        {scheduledTime?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                      </p>
                      {webinar.allow_on_demand && (
                        <div className="text-center mt-6">
                          <Button
                            onClick={() => setViewMode('ondemand')}
                            variant="outline"
                            className="rounded-full border-white/20 text-white hover:bg-white/10 gap-2 text-sm"
                            data-testid="watch-now-btn"
                          >
                            <Eye className="w-4 h-4" /> Watch On Demand Instead
                          </Button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                </div>
              </div>
            )}

            {/* Info below video */}
            <div className="p-4 sm:p-6">
              <h2 className="text-white font-serif text-xl sm:text-2xl mb-2" data-testid="webinar-description-title">{webinar.title}</h2>
              <p className="text-slate-400 text-sm leading-relaxed">{webinar.description}</p>
              {isLive && (
                <div className="flex items-center gap-4 mt-4">
                  <div className="flex items-center gap-1.5 text-red-400 text-sm">
                    <Radio className="w-3.5 h-3.5 animate-pulse" /> Live Now
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Chat Panel */}
          {webinar.chat_enabled && (
            <div className="lg:w-[360px] h-[400px] lg:h-[calc(56.25vw*0.5625+200px)] max-h-[700px] min-h-[400px] bg-white flex flex-col" data-testid="chat-panel">
              <LiveChat chatEnabled={webinar.chat_enabled} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
